import React from 'react'

import { Button, Col, Portlet, Row } from '_zari/core'
import { ZariContext } from '_zari/context'

import * as BackgroundJobsService from 'services/backgroundJobs.service'
import { DownloadAxiosFile } from 'utils'

const portletBreadcrumbs = [{ name: 'Procesos en Background' }]

const statusClass = {
  EN_PROCESO: 'warning',
  COMPLETADO: 'success',
  ERROR: 'danger'
}

const formatDate = value => {
  if (!value) return '-'

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '-'

  return date.toLocaleString()
}

const formatParams = params => {
  const entries = Object.entries(params || {}).filter(([, value]) => value !== '' && value !== null && value !== undefined)

  if (entries.length === 0) return '-'

  return entries.map(([key, value]) => `${key}: ${value}`).join(' | ')
}

const BackgroundJobsMain = () => {
  const __zariContext = React.useContext(ZariContext)

  const [jobs, setJobs] = React.useState([])
  const [loading, setLoading] = React.useState(false)
  const [downloadingId, setDownloadingId] = React.useState('')

  const cargarJobs = React.useCallback(
    (flagSilencioso = false) => {
      setLoading(true)

      BackgroundJobsService.obtenerLista()
        .then(({ data }) => setJobs(data || []))
        .catch(({ data }) => {
          if (!flagSilencioso) {
            __zariContext.snack('error', data?.error || 'No se pudo obtener la lista de procesos.')
          }
        })
        .finally(() => setLoading(false))
    },
    [__zariContext]
  )

  React.useEffect(() => {
    cargarJobs()

    const interval = setInterval(() => cargarJobs(true), 10000)
    return () => clearInterval(interval)
  }, [cargarJobs])

  const clickMarcarTodosVistos = React.useCallback(() => {
    BackgroundJobsService.marcarTodosVistos()
      .then(() => {
        __zariContext.snack('success', 'Procesos finalizados marcados como vistos.')
        cargarJobs(true)
      })
      .catch(({ data }) => __zariContext.snack('error', data?.error || 'No se pudo actualizar el estado de los procesos.'))
  }, [__zariContext, cargarJobs])

  const clickDescargar = React.useCallback(
    id => {
      setDownloadingId(id)

      BackgroundJobsService.descargarResultado(id)
        .then(({ data, headers }) => {
          DownloadAxiosFile.downloadAsBlob(data, headers)
        })
        .catch(({ data }) => __zariContext.snack('error', data?.error || 'No se pudo descargar el archivo del proceso.'))
        .finally(() => setDownloadingId(''))
    },
    [__zariContext]
  )

  const tieneFinalizadosSinVer = jobs.some(job => !job.flagVisto && job.estado !== 'EN_PROCESO')

  return (
    <Portlet>
      <Portlet.Header title='Procesos en Background' breadcrumbs={portletBreadcrumbs} />
      <Portlet.Body>
        <Row className='mb-3'>
          <Col md={12} className='text-right'>
            <Button onClick={() => cargarJobs()}>Actualizar</Button>
            {tieneFinalizadosSinVer && <Button onClick={clickMarcarTodosVistos}>Marcar finalizados como vistos</Button>}
          </Col>
        </Row>
        {loading && jobs.length === 0 ? (
          <div>Cargando procesos...</div>
        ) : jobs.length === 0 ? (
          <div>No hay procesos registrados.</div>
        ) : (
          <div className='table-responsive'>
            <table className='table table-bordered table-sm'>
              <thead>
                <tr>
                  <th>Proceso</th>
                  <th>Estado</th>
                  <th>Mensaje</th>
                  <th>Parametros</th>
                  <th>Inicio</th>
                  <th>Fin</th>
                  <th>Archivo</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map(job => (
                  <tr key={job._id}>
                    <td>{job.nombre || job.tipo}</td>
                    <td>
                      <span className={`badge badge-${statusClass[job.estado] || 'secondary'}`}>{job.estado}</span>
                    </td>
                    <td>{job.mensaje || job.progreso || '-'}</td>
                    <td>{formatParams(job.parametros)}</td>
                    <td>{formatDate(job.fechaCreacion)}</td>
                    <td>{formatDate(job.fechaFinalizacion)}</td>
                    <td>
                      {job.resultado ? (
                        <Button disabled={downloadingId === job._id} onClick={() => clickDescargar(job._id)}>
                          {downloadingId === job._id ? 'Descargando...' : 'Descargar'}
                        </Button>
                      ) : (
                        '-'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Portlet.Body>
    </Portlet>
  )
}

export default React.memo(BackgroundJobsMain)
